import { type FC, useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { useStorePrice } from '@/common/hooks/apple-price';
import { type DicePackageItemType } from '@/common/types/userType';
import { Ticker } from '@/components/common/Ticker/Ticker';
import { StringMain } from '@/components/Typography/Typography';
import { Picture } from '@/components/ui/Picture/Picture';
import {
  RadioButtonContainer,
  type RadioButtonContainerProps,
} from '@/components/ui/RadioButtonContainer/RadioButtonContainer';

import { BuyDicesContext } from '../context';

import css from './styles.module.scss';

import dicePng from '/img/dice-game/dice.png';
import diceWebp from '/img/dice-game/dice.webp';
import flashPng from '/img/dice-game/flash-huge.png';
import flashWebp from '/img/dice-game/flash-huge.webp';

interface OfferItemProps
  extends RadioButtonContainerProps,
    DicePackageItemType {
  index: number;
}

const OfferItem: FC<OfferItemProps> = ({
  index,
  diceCount,
  discountPercent,
  amountUsd,
  amountStars,
  restoreEnergy,
  free,
  ...props
}) => {
  const storePrice = useStorePrice(amountUsd);
  const { t } = useTranslation();
  const { setActiveOfferIndex } = useContext(BuyDicesContext);

  const isFreeOffer = free !== undefined;
  const isClaimed = free === false;

  const selectOffer = (): void => {
    setActiveOfferIndex(index);
  };

  return (
    <div className="relative">
      <RadioButtonContainer {...props} onClick={selectOffer}>
        <div className={css.gridItem}>
          <div className="flex items-center gap-1 pl-2">
            <Picture
              className="w-6 h-6 shrink-0"
              img={dicePng}
              webp={diceWebp}
              alt="dice"
            />
            <StringMain weight="bold">
              {diceCount}
              {discountPercent ? ' +' : ''}
            </StringMain>
          </div>
          {diceCount >= 10 ? (
            <div className="h-8 rounded-[80px] bg-white/10 flex items-center pl-3 gap-1">
              <span className="font-medium text-[10px] text-gold leading-none max-w-11 text-center">
                {t('Full recharge')}
              </span>
              <div className="aspect-square h-full rounded-full flex items-center justify-center bg-white/10">
                <Picture
                  className="w-6 h-6 shrink-0"
                  img={flashPng}
                  webp={flashWebp}
                  alt="flash"
                />
              </div>
            </div>
          ) : (
            <div></div>
          )}
          <StringMain
            weight="bold"
            className={`text-right text-nowrap ml-auto ${
              isFreeOffer ? (isClaimed ? 'text-white/50' : 'text-gold') : ''
            }`}
          >
            {isFreeOffer
              ? isClaimed
                ? t('Claimed')
                : t('Free')
              : (storePrice ?? `$ ${amountUsd}`)}
          </StringMain>
        </div>
      </RadioButtonContainer>
      {!!discountPercent && (
        <Ticker
          variant="gold-small"
          text={`-${discountPercent}%`}
          className={css.ticker}
        />
      )}
    </div>
  );
};

export { OfferItem };
